import { Component, Input, inject, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormsModule } from '@angular/forms';
import { RadioButton } from 'primeng/radiobutton';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { InputNumber, InputNumberModule } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { Slider } from 'primeng/slider';
import { FileUpload } from 'primeng/fileupload';
import { ToggleButton } from 'primeng/togglebutton';
import { SelectButton } from 'primeng/selectbutton';

import { Question, QuestionOption, PatientDetails } from '../../../appoinment-const/appoinment-model';
import { QuestionType, QuestionTemplate } from '../../../appoinment-const/appoinment-enum';
import { AppoimentService } from '../../../appoinment-services/appoiment.service';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'app-dynamic-question',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RadioButton,
    Checkbox,
    InputText,
    InputTextarea,
    InputNumber,
    DatePicker,
    Slider,
    FileUpload,
    SelectButton,
    FormsModule,
    DropdownModule,

    forwardRef(() => DynamicQuestionComponent),
  ],
  templateUrl: './dynamic-question.component.html',
  styleUrl: './dynamic-question.component.css',
})
export class DynamicQuestionComponent implements OnInit, OnChanges {
  @Input() question!: Question;
  @Input() form!: FormGroup;
  @Input() allQuestions: Question[] = [];
  @Input() level: number = 0;
  @Input() patientDetails: PatientDetails | null = null;
  private appointmentService = inject(AppoimentService);

  QuestionType = QuestionType;
  QuestionTemplate = QuestionTemplate;

  height: number | null = null;
  weight: number | null = null;
  bmiValue: number | null = null;
  bmiCategory: string = '';
  bmiColorClass: string = '';
  bmiBackgroundClass: string = '';

  // Dietary Recall State Management
  timeOptions: string[] = [];
  dietaryMeals: DietaryMeal[] = [];

  get control(): any {
    return this.form.get(this.question.id);
  }

  get disableHeight(): boolean {
    if (!this.patientDetails) return false;
    const heightVal = this.patientDetails.height;
    const ageVal = this.patientDetails.age;
    return heightVal !== null && heightVal !== undefined && heightVal > 0 && ageVal > 18;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['patientDetails'] && this.patientDetails) {
      if (this.question.template_type === QuestionTemplate.BmiCalculation) {
        if (this.patientDetails.height) {
          this.height = this.patientDetails.height;
        }
        if (this.patientDetails.weight) {
          this.weight = this.patientDetails.weight;
        }
        this.calculateBMI();
      }
    }
  }

  ngOnInit() {
    // Generate custom 30-min interval times
    this.timeOptions = this.generateTimeOptions();

    // Initialize structured tracking objects for Dietary Recall
    if (
      this.question.template_type === QuestionTemplate.DietryRecall ||
      this.question.question_type === QuestionType.Dietry_Recall
    ) {
      this.initDietaryRecall();
    }

    if (this.question.template_type === QuestionTemplate.Sleep_Input) {
      this.initSleepData();
    }
  }

  // --- Sleep Cycle Logic (Circular Slider) ---
  circleSize = 320;
  radius = this.circleSize / 2 - 40;
  strokeWidth = 35;
  tertiaryColor2 = '#4F46E5'; // Placeholder for primary color from dart

  // Angels in radians. 0 is 3 O'Clock.
  // 9 PM = 270 degrees = 4.71 rad
  // 6 AM = 90 degrees = 1.57 rad
  startAngle = 4.71;
  endAngle = 1.57;

  isDraggingStart = false;
  isDraggingEnd = false;
  noFixedSleepTime = false;

  private center = { x: this.circleSize / 2, y: this.circleSize / 2 };

  initSleepData() {
    const val = this.control?.value;
    if (val === "I don't have a fixed time") {
      this.noFixedSleepTime = true;
    } else if (val && typeof val === 'string' && val.includes('Start time:')) {
      const match = val.match(/Start time: (.*?) - End time: (.*)/);
      if (match && match.length === 3) {
        this.startAngle = this.timeStringToAngle(match[1].trim());
        this.endAngle = this.timeStringToAngle(match[2].trim());
      }
    }
  }

  // Convert "12AM", "12PM", "6AM" etc to angle in radians.
  private timeStringToAngle(timeStr: string): number {
    const match = timeStr.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)$/i);
    if (!match) return 0;

    let hrs = parseInt(match[1], 10);
    const mins = match[2] ? parseInt(match[2], 10) : 0;
    const modi = match[3].toUpperCase();

    if (hrs === 12) {
      hrs = modi === 'AM' ? 0 : 12;
    } else {
      if (modi === 'PM') hrs += 12;
    }

    // Add minutes as fraction
    const totalHours = hrs + mins / 60;

    // Map 0-24 hours to angle (-pi/2 to 3pi/2)
    // 0 hrs (12 AM) is Top (-pi/2)
    const angle = (totalHours * Math.PI) / 12 - Math.PI / 2;
    // Normalize to 0 - 2PI range
    return (angle + 2 * Math.PI) % (2 * Math.PI);
  }

  get formattedStartTime(): string {
    return this.formatTime(this.startAngle);
  }

  get formattedEndTime(): string {
    return this.formatTime(this.endAngle);
  }

  private formatTime(angle: number): string {
    // Convert angle to hour (0-24)
    let normalizedAngle = (angle + Math.PI / 2) % (2 * Math.PI);
    if (normalizedAngle < 0) normalizedAngle += 2 * Math.PI;

    // Nearest 30 mins
    let totalHours = (normalizedAngle * 12) / Math.PI;

    // Optional: snap to 30 mins
    totalHours = Math.round(totalHours * 2) / 2;

    totalHours = totalHours % 24;

    let hrs = Math.floor(totalHours);
    let mins = Math.round((totalHours - hrs) * 60);

    if (mins === 60) {
      hrs = (hrs + 1) % 24;
      mins = 0;
    }

    let displayHour = hrs % 12;
    if (displayHour === 0) displayHour = 12;
    const period = hrs < 12 ? 'AM' : 'PM';

    const minStr = mins === 0 ? '00' : mins.toString().padStart(2, '0');
    // The dart app was only formatting rounded hours, but letting it be precise is better.
    // If strict match to dart: `${displayHour}${period}`
    return mins === 0 ? `${displayHour}${period}` : `${displayHour}:${minStr} ${period}`;
  }

  get sleepDuration(): string | null {
    if (this.noFixedSleepTime) return null;

    let startHour = (((this.startAngle + Math.PI / 2) * 12) / Math.PI) % 24;
    let endHour = (((this.endAngle + Math.PI / 2) * 12) / Math.PI) % 24;

    if (startHour < 0) startHour += 24;
    if (endHour < 0) endHour += 24;

    if (endHour <= startHour) {
      endHour += 24;
    }

    const durationInHours = endHour - startHour;
    const hours = Math.floor(durationInHours);
    const minutes = Math.round((durationInHours - hours) * 60);

    return `${hours}h ${minutes}m`;
  }

  toggleNoFixedSleepTime() {
    this.noFixedSleepTime = !this.noFixedSleepTime;
    if (this.noFixedSleepTime) {
      this.control?.setValue("I don't have a fixed time");
    } else {
      this.updateSleepValues();
    }
    this.control?.markAsDirty();
    this.control?.markAsTouched();
  }

  updateSleepValues() {
    if (this.noFixedSleepTime) {
      this.control?.setValue("I don't have a fixed time");
    } else {
      // Dart payload string format
      this.control?.setValue(
        `Start time: ${this.formattedStartTime} - End time: ${this.formattedEndTime}`,
      );
    }
    this.control?.markAsDirty();
    this.control?.markAsTouched();
  }

  // HTML SVG Arc Pathway
  getArcPath(): string {
    const start = this.polarToCartesian(this.center.x, this.center.y, this.radius, this.endAngle);
    const end = this.polarToCartesian(this.center.x, this.center.y, this.radius, this.startAngle);

    let sweepAngle = this.endAngle - this.startAngle;
    if (sweepAngle < 0) sweepAngle += 2 * Math.PI;

    const largeArcFlag = sweepAngle <= Math.PI ? '0' : '1';

    return [
      'M',
      start.x,
      start.y,
      'A',
      this.radius,
      this.radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
    ].join(' ');
  }

  // Handle Positions
  getHandleX(angle: number): number {
    return this.center.x + this.radius * Math.cos(angle);
  }

  getHandleY(angle: number): number {
    return this.center.y + this.radius * Math.sin(angle);
  }

  private polarToCartesian(
    centerX: number,
    centerY: number,
    radius: number,
    angleInRadians: number,
  ) {
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  }

  // Clock generation
  getClockTicks() {
    const ticks = [];
    for (let i = 0; i < 24; i++) {
      const angle = (i * Math.PI) / 12 - Math.PI / 2;
      const isMain = i % 6 === 0;
      const isEven = i % 2 === 0;
      const markerLength = isMain ? 12 : isEven ? 8 : 5;

      ticks.push({
        x1: this.center.x + (this.radius - markerLength) * Math.cos(angle),
        y1: this.center.y + (this.radius - markerLength) * Math.sin(angle),
        x2: this.center.x + (this.radius - 2) * Math.cos(angle),
        y2: this.center.y + (this.radius - 2) * Math.sin(angle),
        isMain,
      });
    }
    return ticks;
  }

  getSunRays() {
    const rays = [];
    const sunCenter = { x: this.circleSize / 2, y: this.circleSize / 2 + 40 };
    const iconSize = 12;
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      const rayLength = iconSize * 0.25;
      const rayStartMult = 0.55;
      const x1 = sunCenter.x + iconSize * rayStartMult * Math.cos(angle);
      const y1 = sunCenter.y + iconSize * rayStartMult * Math.sin(angle);
      const x2 = sunCenter.x + (iconSize * rayStartMult + rayLength) * Math.cos(angle);
      const y2 = sunCenter.y + (iconSize * rayStartMult + rayLength) * Math.sin(angle);
      rays.push(`M ${x1} ${y1} L ${x2} ${y2}`);
    }
    return rays;
  }

  // Drag Interactions
  private handleDragStart(clientX: number, clientY: number, target: EventTarget | null) {
    if (this.noFixedSleepTime) return;

    const rect = (target as Element).closest('.relative')?.getBoundingClientRect();
    if (!rect) return;

    const x = clientX - rect.left - this.center.x;
    const y = clientY - rect.top - this.center.y;

    const startDist = Math.sqrt(
      Math.pow(x - this.radius * Math.cos(this.startAngle), 2) +
        Math.pow(y - this.radius * Math.sin(this.startAngle), 2),
    );
    const endDist = Math.sqrt(
      Math.pow(x - this.radius * Math.cos(this.endAngle), 2) +
        Math.pow(y - this.radius * Math.sin(this.endAngle), 2),
    );

    const threshold = 50;

    if (startDist <= threshold && endDist <= threshold) {
      if (startDist < endDist) this.isDraggingStart = true;
      else this.isDraggingEnd = true;
    } else if (startDist <= threshold) {
      this.isDraggingStart = true;
    } else if (endDist <= threshold) {
      this.isDraggingEnd = true;
    }
  }

  private handleDragMove(clientX: number, clientY: number, target: EventTarget | null) {
    if (!this.isDraggingStart && !this.isDraggingEnd) return;

    const rect = (target as Element).closest('.relative')?.getBoundingClientRect();
    if (!rect) return;

    const x = clientX - rect.left - this.center.x;
    const y = clientY - rect.top - this.center.y;

    let angle = Math.atan2(y, x);
    if (angle < 0) angle += 2 * Math.PI;

    // Optional: Snap to 15 min increments (PI / 48) or 30 mins (PI / 24)
    const snapValue = Math.PI / 24;
    angle = Math.round(angle / snapValue) * snapValue;

    if (this.isDraggingStart) {
      this.startAngle = angle;
    } else {
      this.endAngle = angle;
    }
  }

  private handleDragEnd() {
    if (this.isDraggingStart || this.isDraggingEnd) {
      this.isDraggingStart = false;
      this.isDraggingEnd = false;
      this.updateSleepValues();
    }
  }

  onMouseDown(event: MouseEvent) {
    this.handleDragStart(event.clientX, event.clientY, event.target);
  }
  onMouseMove(event: MouseEvent) {
    this.handleDragMove(event.clientX, event.clientY, event.target);
  }
  onMouseUp() {
    this.handleDragEnd();
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length > 0) {
      // Prevent scrolling while interacting with the dial
      const rect = (event.target as Element).closest('.relative')?.getBoundingClientRect();
      if (rect) event.preventDefault();

      this.handleDragStart(event.touches[0].clientX, event.touches[0].clientY, event.target);
    }
  }
  onTouchMove(event: TouchEvent) {
    if (this.isDraggingStart || this.isDraggingEnd) {
      event.preventDefault(); // Stop scroll when dragging
      this.handleDragMove(event.touches[0].clientX, event.touches[0].clientY, event.target);
    }
  }
  onTouchEnd() {
    this.handleDragEnd();
  }

  // --- End Sleep Cycle Logic ---

  // --- Dietary Recall Logic ---
  private initDietaryRecall() {
    this.dietaryMeals = [
      {
        id: 'early_morning',
        title: 'Early Morning',
        icon: '🌅',
        example: 'E.g. Upon waking up',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
      {
        id: 'breakfast',
        title: 'Breakfast',
        icon: '🥞',
        example: 'E.g. 8:00 - 10:00 AM',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
      {
        id: 'mid_morning',
        title: 'Mid Morning',
        icon: '☕',
        example: 'E.g. 10:30 - 11:30 AM',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
      {
        id: 'lunch',
        title: 'Lunch',
        icon: '🍛',
        example: 'E.g. 12:00 - 2:30 PM',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
      {
        id: 'evening_snack',
        title: 'Evening Snack',
        icon: '🥪',
        example: 'E.g. 4:00 - 6:30 PM',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
      {
        id: 'dinner',
        title: 'Dinner',
        icon: '🍽️',
        example: 'E.g. 7:00 - 9:30 PM',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
      {
        id: 'late_night',
        title: 'Late Night (Optional)',
        icon: '🌙',
        example: 'E.g. After 9:30 PM',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
      {
        id: 'other_fluids',
        title: 'Other Fluids & Beverages',
        icon: '🥤',
        example: 'Tea, coffee, juice, soft drinks',
        entries: [{ startTime: null, endTime: null, details: '' }],
      },
    ];

    // Attempt to parse existing value if it is a JSON string
    if (this.control?.value && typeof this.control.value === 'string') {
      try {
        const parsed = JSON.parse(this.control.value);
        if (Array.isArray(parsed) && parsed.length === this.dietaryMeals.length) {
          this.dietaryMeals = parsed;
        }
      } catch (e) {
        // Fallback or ignore if value isn't valid JSON (perhaps old textarea dump)
      }
    }
  }

  private generateTimeOptions(): string[] {
    const times: string[] = [];
    const periods = ['AM', 'PM'];
    for (const period of periods) {
      for (let hour = 1; hour <= 12; hour++) {
        // Handle 12 differently for AM/PM sequencing if extremely strict,
        // but standard format works fine: 12:00 AM, 12:30 AM, 1:00 AM, etc.
        const h = hour === 12 ? 12 : hour;
        times.push(`${h}:00 ${period}`);
        times.push(`${h}:30 ${period}`);
      }
    }
    // Minor sort or specific ordering (12 AM -> 1 AM ... 11 PM)
    const sortedTimes = [];
    for (const p of ['AM', 'PM']) {
      sortedTimes.push(`12:00 ${p}`, `12:30 ${p}`);
      for (let i = 1; i <= 11; i++) {
        sortedTimes.push(`${i}:00 ${p}`, `${i}:30 ${p}`);
      }
    }
    return sortedTimes;
  }

  addDietaryEntry(mealIndex: number) {
    this.dietaryMeals[mealIndex].entries.push({ startTime: null, endTime: null, details: '' });
    this.updateDietaryRecall();
  }

  removeDietaryEntry(mealIndex: number, entryIndex: number) {
    this.dietaryMeals[mealIndex].entries.splice(entryIndex, 1);
    this.updateDietaryRecall();
  }

  updateDietaryRecall() {
    this.control?.setValue(JSON.stringify(this.dietaryMeals));
    this.control?.markAsDirty();
    this.control?.markAsTouched();
  }

  // --- End Dietary Recall Logic ---

  get childQuestions(): Question[] {
    const selectedValue = this.control?.value;
    if (!selectedValue) return [];

    // Find options that have a child_question_id and are currently selected
    const optionsWithChildren = this.question.questionOptions.filter(
      (opt: QuestionOption) => opt.child_question_id,
    );

    let activeChildIds: string[] = [];

    if (
      this.question.question_type === QuestionType.Single_Choice ||
      this.question.question_type === QuestionType.Yes_No
    ) {
      const selectedOption = optionsWithChildren.find(
        (opt: QuestionOption) => opt.value === selectedValue,
      );
      if (selectedOption?.child_question_id) {
        activeChildIds.push(selectedOption.child_question_id);
      }
    } else if (this.question.question_type === QuestionType.Multi_Choice) {
      const selectedValues = Array.isArray(selectedValue) ? selectedValue : [];
      activeChildIds = optionsWithChildren
        .filter((opt: QuestionOption) => selectedValues.includes(opt.value))
        .map((opt: QuestionOption) => opt.child_question_id!)
        .filter((id: string) => !!id);
    }

    return this.allQuestions.filter((q) => activeChildIds.includes(q.id));
  }

  toggleOption(value: string) {
    if (this.question.question_type === QuestionType.Single_Choice) {
      this.control?.setValue(value);
    } else if (this.question.question_type === QuestionType.Multi_Choice) {
      const currentValues = Array.isArray(this.control?.value) ? [...this.control?.value] : [];
      const index = currentValues.indexOf(value);
      if (index === -1) {
        currentValues.push(value);
      } else {
        currentValues.splice(index, 1);
      }
      this.control?.setValue(currentValues);
    }
  }

  isOptionSelected(value: string): boolean {
    const selectedValue = this.control?.value;
    if (this.question.question_type === QuestionType.Single_Choice) {
      return selectedValue === value;
    } else if (this.question.question_type === QuestionType.Multi_Choice) {
      return Array.isArray(selectedValue) && selectedValue.includes(value);
    }
    return false;
  }

  // calculateBMI() {
  //   if (this.height && this.weight) {
  //     // Assuming height is in cm and weight is in kg
  //     const heightInMeters = this.height / 100;
  //     this.bmiValue = parseFloat((this.weight / (heightInMeters * heightInMeters)).toFixed(1));
  //     this.updateBMICategory();
  //     this.control?.setValue(
  //       `height: ${this.height}cm, weight: ${this.weight}kg, BMI: ${this.bmiValue} (${this.bmiCategory})`,
  //     );
  //   } else {
  //     this.bmiValue = null;
  //     this.bmiCategory = '';
  //     this.control?.setValue('');
  //   }
  // }

  calculateBMI() {
    if (this.height && this.weight) {
      // Height is in cm
      const heightInMeters = this.height / 100;
      const bmi = this.weight / (heightInMeters * heightInMeters);
      this.bmiValue = parseFloat(bmi.toFixed(1));

      this.updateBMICategory();

      // Store a structured string for submission as requested
      this.control?.setValue(
        `height: ${this.height}cm, weight: ${this.weight}kg, BMI: ${this.bmiValue} (${this.bmiCategory})`,
      );
      this.control?.markAsDirty();
      this.control?.markAsTouched();
    } else {
      this.bmiValue = null;
      this.bmiCategory = '';
      this.bmiColorClass = '';
      this.bmiBackgroundClass = '';
      this.control?.setValue(null);
    }
  }

  updateBMICategory() {
    if (!this.bmiValue) return;
    if (this.bmiValue < 18.5) {
      this.bmiCategory = 'Underweight';
      this.bmiColorClass = 'text-blue-700';
      this.bmiBackgroundClass = 'bg-blue-50 border-blue-200';
    } else if (this.bmiValue >= 18.5 && this.bmiValue < 25) {
      this.bmiCategory = 'Normal';
      this.bmiColorClass = 'text-green-700';
      this.bmiBackgroundClass = 'bg-green-50 border-green-200';
    } else if (this.bmiValue >= 25 && this.bmiValue < 30) {
      this.bmiCategory = 'Overweight';
      this.bmiColorClass = 'text-yellow-700';
      this.bmiBackgroundClass = 'bg-yellow-50 border-yellow-200';
    } else if (this.bmiValue >= 30 && this.bmiValue < 35) {
      this.bmiCategory = 'Obese I – High risk';
      this.bmiColorClass = 'text-red-700';
      this.bmiBackgroundClass = 'bg-red-50 border-red-200';
    } else {
      this.bmiCategory = 'Obese II – Very High risk';
      this.bmiColorClass = 'text-red-800';
      this.bmiBackgroundClass = 'bg-red-100 border-red-300';
    }
  }

  onFileUpload(event: any, position?: string) {
    const file = event.files?.[0];
    if (!file) return;

    if (this.question.template_type === QuestionTemplate.FullBodyImage && position) {
      const currentValues =
        this.control?.value && typeof this.control.value === 'object'
          ? { ...this.control.value }
          : {};
      currentValues[position] = event.files[0];
      this.control?.setValue(currentValues);

      currentValues[position] = {
        file: file,
        name: file.name,
      };

      // If all 3 images are present, we could trigger the API call here
      if (currentValues.front?.file && currentValues.left?.file && currentValues.right?.file) {
        this.appointmentService
          .uploadBodyImages(
            currentValues.front.file,
            currentValues.left.file,
            currentValues.right.file,
          )
          .subscribe({
            next: (res) => {
              const data = res.data || res;
              const updatedValue = {
                ...currentValues,
                report_url: data.reportUrl || data.report_url || null,
              };

              // Map the blob URLs from the API response so the payload builder can use them
              if (updatedValue.front) updatedValue.front.url = data.frontProfile;
              if (updatedValue.left) updatedValue.left.url = data.leftProfile;
              if (updatedValue.right) updatedValue.right.url = data.rightProfile;

              this.control?.setValue(updatedValue);
            },
          });
      }
    } else {
      this.control?.setValue(event.files);
    }
    this.control?.markAsDirty();
    this.control?.markAsTouched();
  }

  getUnicodeIcon(hex: string | undefined): string {
    if (!hex) return '';
    try {
      return String.fromCodePoint(parseInt(hex, 16));
    } catch (e) {
      return '';
    }
  }
}

// Interfaces for Dietary Recall
export interface DietaryEntry {
  startTime: string | null;
  endTime: string | null;
  details: string;
}

export interface DietaryMeal {
  id: string;
  title: string;
  icon: string;
  example: string;
  entries: DietaryEntry[];
}
